/**
 * Auth request schemas — shared between the API (server-side validation) and
 * the web SPA (client-side form validation, see issue #10).
 *
 * Spec §5 Authentication. Signup is gated by a domain allowlist
 * (`SIGNUP_ALLOWED_DOMAINS`, issue #268) plus a hardcoded `SIGNUP_CODE`; both
 * are checked server-side and never validated client-side. The schemas here
 * only enforce shape (valid email, password length) — the domain-allowlist
 * and signup-code checks live in the auth service.
 *
 * Password length: 8+ characters as the baseline UX bound. OWASP's no-MFA
 * recommendation is higher; we stay close to it elsewhere (bcrypt cost) but
 * keep the form bar low for the single-operator deployment model. Bump if
 * the threat model changes. The upper bound (200) protects bcrypt — which
 * silently truncates inputs above 72 bytes — from being abused as a DoS
 * vector when paired with a high cost factor.
 */

import { z } from "zod";

const trimmedEmail = z.string().trim().toLowerCase().email();
const password = z.string().min(8).max(200);

export const signupSchema = z.object({
  email: trimmedEmail,
  password,
  signupCode: z.string().min(1),
  /**
   * Optional org-invite token (issue #276 — multi-tenancy Phase 5). When
   * the signup URL carries `?invite=<token>`, the SPA threads the token
   * here and the API atomically (a) creates the User + personal Org +
   * OWNER membership as usual, and (b) inserts an additional MEMBER
   * membership in the invitation's org + marks the invitation accepted.
   *
   * Trim + bounded length so a 10MB blob can't slip in. Validation of
   * shape vs. validity is split: this schema only proves the field is a
   * non-empty bounded string; the auth service does the lookup +
   * expiry / accepted / email-mismatch checks.
   */
  inviteToken: z.string().trim().min(1).max(200).optional(),
});

export const loginSchema = z.object({
  email: trimmedEmail,
  password: z.string().min(1).max(200),
});

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Public-facing user shape returned by the API. Never includes `passwordHash`.
 * Mirrors the User row minus the credential fields.
 */
export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
  updatedAt: string;
};
